import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {createNestedUpdateState} from "../../../utilities/componentFunctions";
import HTMLTextInput from "../../common/HTMLTextInput/HTMLTextInput";
import {arrayIsNotEmptyNorFalsy, getValues} from "../../../utilities/helperFunctions";
import {ListDropdown} from "../../common/Dropdown/Dropdown";
import {httpVerb} from "../../../utilities/constants";
import JobScheduleModel from "../../../models/job/JobScheduleModel";
import {useDispatch} from "react-redux";
import Checkbox from "../../common/Checkbox/Checkbox";
import {
    useClearOnFirstCallback,
    useDropdownHandler,
    useInputHandler,
    useToggleHandler,
    useUpdateHandler
} from "../../../utilities/formHooks";
import {InputTableV2} from "../../common/InputTable/InputTable";
import TextArea from "../../common/TextArea/TextArea";
import DatasetModel from "../../../models/data/DatasetModel";
import Collapsable from "../../common/Collapsable/Collapsable";
import ScriptPaneField from "../../workflowBuilder/components/ScriptPaneField";
import FormBuilderComponentConfiguration from "../../../models/workflowbuilder/FormBuilderComponentConfiguration";

function WebhookTriggerPanel(props) {
    const dispatch = useDispatch();
    const {t} = useTranslation(['jobSchedule']);

    const {
        webhookTrigger,
        updateState
    } = props;

    const clearOnFirstCallback = useClearOnFirstCallback();
    const updateWebhookTriggerState = createNestedUpdateState(updateState, ['webhookTrigger']);

    const updateHandler = useUpdateHandler({
        updateState: updateWebhookTriggerState
    });
    const dropdownHandler = useDropdownHandler({updateHandler});
    const inputHandler = useInputHandler({updateHandler});
    const toggleHandler = useToggleHandler({updateHandler});

    const signatureKeyRef = useRef();
    // onMount decrypt, unMount encrypt
    useLayoutEffect(() => {
        dispatch(JobScheduleModel.actionCreators.decryptProtectedValues('webhookTrigger'));
        return () => {
            if (signatureKeyRef.current) {
                dispatch(JobScheduleModel.actionCreators.encryptProtectedValues('webhookTrigger', {signatureKey: signatureKeyRef.current.value}));
            }
        }
    }, []);

    useEffect(() => {
        if (!webhookTrigger.useSignatureKey) {
            updateWebhookTriggerState({signatureKey: ''});
        }
    }, [webhookTrigger.useSignatureKey]);

    useEffect(() => {
        if (webhookTrigger.useSignatureKey && webhookTrigger.httpVerb === httpVerb.GET) {
            updateWebhookTriggerState({useSignatureKey: false});
        }
    }, [webhookTrigger.httpVerb]);

    useEffect(() => {
        if (arrayIsNotEmptyNorFalsy(webhookTrigger.headers)) {
            for (let i=0; i<webhookTrigger.headers.length;i++ ) {
                const name = webhookTrigger.headers[i][0].value;
                const value = webhookTrigger.headers[i][1].value;
                if (name === 'Content-Type') {
                    const scriptLanguage = value.replace("application/", "");
                    setLanguage(scriptLanguage);
                    break;
                }
            }
        }
    }, [webhookTrigger.headers]);

    function setHeaders (update) {
        const updates = {
            headers: update(webhookTrigger.headers)
        }

        updateHandler(updates);
    }

    const [language, setLanguage] = useState("json")
    const isUseSignatureKeyDisabled = webhookTrigger.httpVerb === httpVerb.GET;
    const isSignatureKeyDisabled = isUseSignatureKeyDisabled || !webhookTrigger.useSignatureKey;

    return (
        <>
            <div className="display-input" style={{display: 'flex', alignItems: 'center'}}>
                <label className="label" style={{marginRight: '0.75rem'}}>
                    {t('jobSchedule:label.httpVerb')}:
                </label>
                <HttpVerbDropdown value={webhookTrigger.httpVerb} onItemSelect={dropdownHandler}/>
            </div>

            <Checkbox label={t('jobSchedule:label.useSignatureKey')} name={'useSignatureKey'} value={webhookTrigger.useSignatureKey}
                onChange={toggleHandler} isDisabled={isUseSignatureKeyDisabled}/>

            {webhookTrigger.useSignatureKey &&
                <div className="display-input">
                    <HTMLTextInput ref={signatureKeyRef} label={t('jobSchedule:label.signatureKey')} name={'signatureKey'} onBeforeInput={clearOnFirstCallback}
                        type={'password'} defaultValue={webhookTrigger.signatureKey} onChange={inputHandler} isDisabled={isSignatureKeyDisabled}
                    />
                </div>
            }

            <Collapsable label={t('jobSchedule:label.response')} style={{marginTop:"1rem"}}>
                <div className="display-input">
                    <InputTableV2 headers={[{value: t('common:label.name')}, {value: t('common:label.value')}, {value: t('common:label.description')}]}
                                  inputTable={webhookTrigger.headers} setInputTable={setHeaders}
                                  defaultRow={JobScheduleModel.defaultWebhookTriggerHeaderRow} isDisabled={false}
                                  ariaLabelKey={'WebhookTriggerHeaders'} label={t('jobSchedule:label.headers')}
                    />
                </div>

                <div className="display-input" style={{display: 'flex', alignItems: 'center'}}>
                    <label className="label" style={{marginRight: '0.75rem'}}>
                        {t('jobSchedule:label.httpResponseCode')}:
                    </label>
                    <HTMLTextInput style={{width: '8rem'}} aria-label={t('jobSchedule:label.httpResponseCode')} type={"number"} name={"responseCode"} value={webhookTrigger.responseCode} onChange={inputHandler}/>
                </div>

                <div className="display-input">
                    <ScriptPaneField id={"webhookTriggerBody"} data-type={FormBuilderComponentConfiguration.ComponentType.SCRIPT_PANE} label={t('jobSchedule:label.body')}
                                     configuration={{name: "body"}} value={webhookTrigger.body} updateState={updateHandler} scriptLanguage={language}
                    />
                </div>
            </Collapsable>
        </>
    )
}

function HttpVerbDropdown(props) {
    const {t} = useTranslation(['aria', 'jobSchedule']);

    const httpVerbItems = getValues(httpVerb)
        .map(verb => ({
            name: verb,
            value: verb
        }));

    return (
        <ListDropdown id={'httpVerbDropdown'} name={'httpVerb'} aria-label={t('aria:hiddenAssistText.httpVerb')}
            noneSelectedMessage={t('jobSchedule:option.selectHttpVerb')} items={httpVerbItems} {...props}/>
    )
}

export default WebhookTriggerPanel;
